import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import {
  AppDatePicker,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import Translator from '../../../../../components/helpers/Translator';
import { axiosPath } from '../../../../../../routing/helpers/router';
import minions from '../../../../../../minions/minions';
import errorHandler from '../../../../../components/error/ErrorHandler';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import { handleMinDate } from '../../tools/comsaReportsTools';

export default function ModuleComsaRecycledPaperReportAdd(props) {
  const { onRechargeReports, moduleData } = props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [showModalReport, setShowModalReport] = useState(false);
  const type = 'recycledPaper';
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator file="user" name={`calendar.month${i + 1}`} />,
      value: i + 1,
    })),
  );
  const validationSchema = Yup.object().shape({
    year: Yup.date()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="m-cr-required" />,
        ),
      ),
    month: Yup.date()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="m-cr-required" />,
        ),
      ),
    total: Yup.string()
      .nullable()
      .required(
        ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="m-cr-required" />,
        ),
      ),
  });

  const handleAddReport = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath('api_r_user_module_comsaReports_addReport'),
          minions.forms.toFormData({
            ...payload,
            type: type,
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        .then(() => {
          if (isMounted.current) onRechargeReports();
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setShowModalReport(false)),
    [[moduleBridgeHex, subModuleBridgeHex, plantHex, type]],
  );

  return (
    <>
      <div className="row">
        <div className="col-12">
          <span
            className="btn btn-success btn-sm mr-2"
            onClick={() => setShowModalReport(true)}
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-comsaReports"
                name="m-cr-new.recycled.paper"
              />,
            )}
          >
            <AppRemixIcon icon="add" />
          </span>
        </div>
      </div>

      <AppModal
        show={showModalReport}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="m-cr-new.recycled.paper" />,
        )}
        size="lg"
        onClose={() => setShowModalReport(false)}
      >
        <Formik
          initialValues={{
            year: null,
            month: null,
            total: '',
            comment: '',
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleAddReport}
        >
          {({ setFieldValue, handleSubmit, values, errors }) => (
            <>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppDatePicker
                      title={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="year" />,
                      )}
                      value={values.year}
                      onChange={(opt) => setFieldValue('year', opt)}
                      minDate={handleMinDate(moduleData)}
                      dateFormat="yyyy"
                      displayDateFormat="YYYY"
                      placeholder={
                        <Translator file="module-comsaReports" name="select.a.year" />
                      }
                      isClearable
                      showYearPicker
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      error={errors.year}
                    />
                  </div>
                </div>
                <div className="col-6">
                  <div className="mb-3">
                    <AppSingleSelect
                      title={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="month" />,
                      )}
                      options={months}
                      onChange={(opt) => setFieldValue('month', opt)}
                      icon={<AppRemixIcon icon="calendar" />}
                      placeholder={
                        <Translator
                          file="module-comsaReports"
                          name="select.a.month"
                        />
                      }
                      value={values.month}
                      error={errors.month}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <AppInputNumber
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="m-cr-quantity" />,
                    )}
                    icon={<AppRemixIcon icon="scales" />}
                    value={values.total}
                    onChange={(value) => setFieldValue('total', value)}
                    placeholder={
                      <Translator file="module-comsaReports" name="m-cr-quantity" />
                    }
                    decimals={3}
                    append={
                      <div className="input-group-append">
                        <span className="input-group-text">
                          <Translator file="module-comsaReports" name="m-cr-tons" />
                        </span>
                      </div>
                    }
                  />
                  {errors.total && <div className="text-danger">*{errors.total}</div>}
                </div>
                <div className="col-12">
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="m-cr-comment" />,
                    )}
                    value={values?.comment}
                    onChange={(v) => setFieldValue(`comment`, v)}
                    height="89px"
                  />
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-block"
                    type="submit"
                    onClick={handleSubmit}
                  >
                    <Translator file="module-comsaReports" name="add" />
                  </button>
                </div>
              </div>
            </>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
